import { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Check, X, Link } from 'lucide-react';
import type { IClaim } from '@/@types/claims';

interface IApproveDialogProps {
  preAuthId: Id<'preAuthorizations'>;
  estimatedCost: number;
  currency: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApproveDialog({
  preAuthId,
  estimatedCost,
  currency,
  open,
  onOpenChange,
}: IApproveDialogProps) {
  const approvePreAuth = useMutation(api.insurance.preAuthorizations.approve);
  const [amount, setAmount] = useState(String(estimatedCost));
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [isPending, setIsPending] = useState(false);

  async function handleConfirm() {
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || !validUntil) return;
    setIsPending(true);
    try {
      await approvePreAuth({
        preAuthId,
        approvedAmount: parsedAmount,
        validUntil: new Date(validUntil).getTime(),
        internalNotes: notes || undefined,
      });
      onOpenChange(false);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve Pre-Authorization</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="approved-amount">Approved Amount ({currency})</Label>
            <Input
              id="approved-amount"
              type="number"
              min="0"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="valid-until">Valid Until</Label>
            <Input
              id="valid-until"
              type="date"
              value={validUntil}
              onChange={e => setValidUntil(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="approve-notes">Internal Notes (optional)</Label>
            <Textarea
              id="approve-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add any internal notes..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={isPending || !amount || !validUntil}
          >
            <Check className="mr-1.5 h-4 w-4" />
            {isPending ? 'Approving...' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface IDenyDialogProps {
  preAuthId: Id<'preAuthorizations'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DenyDialog({ preAuthId, open, onOpenChange }: IDenyDialogProps) {
  const denyPreAuth = useMutation(api.insurance.preAuthorizations.deny);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isPending, setIsPending] = useState(false);

  async function handleConfirm() {
    if (!reason.trim()) return;
    setIsPending(true);
    try {
      await denyPreAuth({
        preAuthId,
        denialReason: reason.trim(),
        internalNotes: notes || undefined,
      });
      onOpenChange(false);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deny Pre-Authorization</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="denial-reason">Denial Reason</Label>
            <Textarea
              id="denial-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Explain why the request is being denied..."
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deny-notes">Internal Notes (optional)</Label>
            <Textarea
              id="deny-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add any internal notes..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending || !reason.trim()}
          >
            <X className="mr-1.5 h-4 w-4" />
            {isPending ? 'Denying...' : 'Deny'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ILinkClaimDialogProps {
  preAuthId: Id<'preAuthorizations'>;
  claims: IClaim[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LinkClaimDialog({ preAuthId, claims, open, onOpenChange }: ILinkClaimDialogProps) {
  const linkToClaim = useMutation(api.insurance.preAuthorizations.linkToClaim);
  const [selectedClaimId, setSelectedClaimId] = useState('');
  const [isPending, setIsPending] = useState(false);

  async function handleConfirm() {
    if (!selectedClaimId) return;
    setIsPending(true);
    try {
      await linkToClaim({
        preAuthId,
        claimId: selectedClaimId as Id<'claims'>, // AS_JUSTIFIED: value comes from claims list returned by Convex, guaranteed to be valid Id<'claims'>
      });
      onOpenChange(false);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link to Claim</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="select-claim">Select Claim</Label>
            <Select value={selectedClaimId} onValueChange={setSelectedClaimId}>
              <SelectTrigger id="select-claim">
                <SelectValue placeholder="Choose a claim..." />
              </SelectTrigger>
              <SelectContent>
                {claims.map(c => (
                  <SelectItem key={c._id} value={c._id}>
                    {c.claimNumber} — {c.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {claims.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No claims found for this policy.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={isPending || !selectedClaimId}
          >
            <Link className="mr-1.5 h-4 w-4" />
            {isPending ? 'Linking...' : 'Link Claim'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
